import { useEffect, useState } from 'react';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import Sticky from 'react-sticky-el';
import { rgba } from 'polished';
import styled from 'styled-components';

import { SIGNUP_LINK, STICKY } from '../../utils';
import { Button, Heading } from '../design-system';

const Wrapper = styled.aside`
  background: ${({ theme }) => theme.backgroundColor};
  display: block;

  @media (max-width: 1410px) {
    left: 1%;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

const Inner = styled.div`
  background: ${({ theme }) => theme.backgroundColor};
  border: 1px solid ${({ theme }) => theme.borderColor.MUTED};
  padding: 30px;
`;

const StyledSticky = styled(Sticky)`
  width: 20vw;

  nav {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  ul {
    margin-bottom: 30px;
    box-shadow: inset 1px 0 1px ${({ theme }) => theme.borderColor.MUTED};

    li {
      list-style: none;
      margin: 0;
      transition: background-color ${({ theme }) => theme.animation.DURATION}
        ${({ theme }) => theme.animation.EASING};

      a {
        display: block;
        color: ${({ theme }) => theme.fontColor.DEFAULT};
        text-decoration: none;
        border: none;
        padding: 4px 12px;
        width: 100%;

        &:hover,
        &:active {
          background: none;
        }
      }

      &:hover {
        background-color: ${({ theme }) => rgba(theme.highlightColor, 0.04)};
      }

      &.active {
        background-color: ${({ theme }) => rgba(theme.highlightColor, 0.08)};
      }
    }
  }

  .isSticky {
    background: ${({ theme }) => theme.backgroundColor};

    ${Inner} {
      margin-top: 100px;
    }
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    max-width: 15vw;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    width: 100%;
  }
`;

export interface AsideHeading {
  id: string;
  text: string;
}

interface BlogPostAsideProps {
  headings: AsideHeading[];
}

export const BlogPostAside: React.ComponentType<BlogPostAsideProps> =
  React.memo(({ headings }) => {
    const [activeHeadingId, setActiveHeadingId] = useState<string>('');

    useEffect(() => {
      const handleHashChange = () => {
        setActiveHeadingId(window.location.hash.replace('#', ''));
      };
      window.addEventListener('hashchange', handleHashChange);

      handleHashChange();

      return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    return (
      <Wrapper>
        <Helmet
          htmlAttributes={{
            class: 'smoothScroll'
          }}
        />
        <StyledSticky
          boundaryElement={`.${STICKY.Boundary}`}
          stickyClassName={STICKY.Element}
          stickyStyle={{ zIndex: 1 }}
          topOffset={-100}>
          <Inner>
            <nav>
              <Heading renderAs="h5">In this article</Heading>
              <ul>
                {headings.map(heading => (
                  <li
                    key={heading.id}
                    className={activeHeadingId === heading.id ? 'active' : ''}>
                    <a
                      className={activeHeadingId === heading.id ? 'active' : ''}
                      href={`#${heading.id}`}>
                      {heading.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <Button
              link={`${SIGNUP_LINK}?source=blog-toc&_gl=1*umfwd1*_gcl_au*MTY3ODkyNjE4NC4xNzA0MzkwNzgy`}
              size="large"
              stylez="loud"
              text="Get started for free"
            />
          </Inner>
        </StyledSticky>
      </Wrapper>
    );
  });
